import { createTheme } from '@mui/material/styles';

// Brand color constants
export const BRAND_COLORS = {
  forestGreen: {
    main: '#003D2D',  // Pantone 3435 C
    rgb: 'rgb(0, 61, 45)',
    cmyk: 'C95 M45 Y77 K60'
  },
  yellow: {
    main: '#FFC72C',  // Pantone 123 C
    rgb: 'rgb(255, 199, 44)',
    cmyk: 'C0 M24 Y86 K0'
  },
  white: {
    main: '#FFFFFF',
    rgb: 'rgb(255, 255, 255)',
    cmyk: 'C0 M0 Y0 K0'
  }
};

// Helper function to convert hex to rgba
export const alpha = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Derived semantic colors
export const SEMANTIC_COLORS = {
  primary: BRAND_COLORS.forestGreen.main,
  secondary: BRAND_COLORS.yellow.main,
  background: BRAND_COLORS.white.main,
  text: {
    primary: BRAND_COLORS.forestGreen.main,
    secondary: '#4A5568',
    light: '#718096'
  },
  button: {
    primary: {
      background: BRAND_COLORS.forestGreen.main,
      text: BRAND_COLORS.white.main,
      hover: '#002B20'
    },
    secondary: {
      background: BRAND_COLORS.yellow.main,
      text: BRAND_COLORS.forestGreen.main,
      hover: '#E6B327'
    }
  }
};

// Create the Material-UI theme
export const theme = createTheme({
  palette: {
    primary: {
      main: SEMANTIC_COLORS.primary,
      light: alpha(SEMANTIC_COLORS.primary, 0.7),
      dark: '#002B20',
      contrastText: BRAND_COLORS.white.main,
    },
    secondary: {
      main: SEMANTIC_COLORS.secondary,
      light: alpha(SEMANTIC_COLORS.secondary, 0.7),
      dark: '#E6B327',
      contrastText: SEMANTIC_COLORS.primary,
    },
    background: {
      default: alpha(BRAND_COLORS.forestGreen.main, 0.05),
      paper: BRAND_COLORS.white.main,
    },
    text: {
      primary: SEMANTIC_COLORS.text.primary,
      secondary: SEMANTIC_COLORS.text.secondary,
    },
    success: {
      main: '#4caf50',
      light: '#e8f5e8',
    },
    error: {
      main: '#f44336',
      light: '#ffebee',
    },
  },
  typography: {
    fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 900, // Black
      fontSize: '2.5rem',
      color: SEMANTIC_COLORS.text.primary,
    },
    h2: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '2rem',
      color: SEMANTIC_COLORS.text.primary,
    },
    h3: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 600, // Demi
      fontSize: '1.75rem',
      color: SEMANTIC_COLORS.text.primary,
    },
    h4: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 600, // Demi
      fontSize: '1.5rem',
    },
    h5: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500, // Medium
      fontSize: '1.25rem',
    },
    h6: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500, // Medium
      fontSize: '1.125rem',
    },
    body1: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400, // Regular
      fontSize: '1rem',
    },
    body2: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300, // Light
      fontSize: '0.875rem',
    },
    button: {
      fontFamily: '"Industry", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 600, // Demi
      textTransform: 'none',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 61, 45, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 61, 45, 0.3)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 61, 45, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 61, 45, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: SEMANTIC_COLORS.primary,
        },
        thumb: {
          backgroundColor: SEMANTIC_COLORS.primary,
          '&:hover': {
            boxShadow: `0 0 0 8px ${alpha(SEMANTIC_COLORS.primary, 0.16)}`,
          },
        },
        track: {
          backgroundColor: SEMANTIC_COLORS.primary,
        },
        rail: {
          backgroundColor: alpha(SEMANTIC_COLORS.primary, 0.2),
        },
      },
    },
  },
});
